import { base64ToBytes } from '~/utils.js';
import { canonizeAddress } from '~/tonweb.js';

const BOC_MAGIC = 'b5ee9c72';

// addr_std$10 anycast:(Maybe Anycast) workchain_id:int8 address:bits256
const ADDRESS_BITS = 2 + 1 + 8 + 256;

/**
 * Reads the bit string of the root cell of a single-root, refless BOC.
 * That's the only shape we need: toncenter returns get-method addresses
 * as one standalone cell. Anything more complex (dictionaries, code) is
 * rejected instead of being half-parsed.
 *
 * @param  {String} boc  base64 serialized bag of cells
 * @return {String|null} bit string, e.g. '10000000...'
 */
const readRootCellBits = function readBitsOfSingleCellBoc(boc) {
    const bytes = base64ToBytes(boc);

    if (bytes.length < 8) {
        return null;
    }

    let magic = '';
    for (let i = 0; i < 4; i += 1) {
        magic += bytes[i].toString(16).padStart(2, '0');
    }

    if (magic !== BOC_MAGIC) {
        return null;
    }

    const flags = bytes[4];
    const refByteSize = flags & 7; // eslint-disable-line no-bitwise
    const hasIndex = Boolean(flags & 128); // eslint-disable-line no-bitwise
    const offsetByteSize = bytes[5];

    const readInt = (offset, size) => {
        let value = 0;
        for (let i = 0; i < size; i += 1) {
            value = (value * 256) + bytes[offset + i];
        }
        return value;
    };

    let cursor = 6;
    const cellCount = readInt(cursor, refByteSize);
    cursor += refByteSize;
    const rootCount = readInt(cursor, refByteSize);
    cursor += refByteSize * 2; // skip root count + absent count
    cursor += offsetByteSize; // skip total cells size

    if (cellCount !== 1 || rootCount !== 1) {
        return null;
    }

    cursor += rootCount * refByteSize; // skip root indexes
    if (hasIndex) {
        cursor += cellCount * offsetByteSize;
    }

    const d1 = bytes[cursor];
    const d2 = bytes[cursor + 1];

    // Exotic cells and cells with refs are out of scope:
    if (d1 === undefined || d2 === undefined || (d1 & 7) !== 0 || (d1 & 8) !== 0) { // eslint-disable-line no-bitwise
        return null;
    }

    const dataByteLength = Math.ceil(d2 / 2);
    const isAugmented = d2 % 2 === 1;
    const data = bytes.subarray(cursor + 2, cursor + 2 + dataByteLength);

    if (data.length !== dataByteLength) {
        return null;
    }

    let bits = '';
    for (let i = 0; i < data.length; i += 1) {
        bits += data[i].toString(2).padStart(8, '0');
    }

    if (isAugmented) {
        // The last byte is padded with a single 1 bit followed by zeroes:
        const lastOne = bits.lastIndexOf('1');
        bits = lastOne === -1 ? '' : bits.slice(0, lastOne);
    }

    return bits;
};

/**
 * Decodes a MsgAddressInt returned by a get-method into a canonical
 * user-friendly address. addr_none and anything unparseable yield null,
 * so callers can render an empty value instead of a broken link.
 *
 * @param  {String} boc  base64 serialized cell holding a single address
 * @return {String|null}
 */
export const parseAddressCell = function parseMsgAddressIntCell(boc) {
    if (typeof boc !== 'string' || boc.length === 0) {
        return null;
    }

    let bits;

    try {
        bits = readRootCellBits(boc);
    } catch (error) {
        console.error('Failed to read address cell:', error);
        return null;
    }

    if (!bits || bits.length < ADDRESS_BITS || bits.slice(0, 3) !== '100') {
        return null;
    }

    const rawWorkchain = parseInt(bits.slice(3, 11), 2);
    const workchain = rawWorkchain > 127 ? rawWorkchain - 256 : rawWorkchain;

    let hash = '';
    for (let i = 0; i < 32; i += 1) {
        const offset = 11 + (i * 8);
        hash += parseInt(bits.slice(offset, offset + 8), 2).toString(16).padStart(2, '0');
    }

    try {
        return canonizeAddress(`${workchain}:${hash}`);
    } catch (error) {
        console.error('Failed to canonize address from cell:', error);
        return null;
    }
};

/**
 * DeDust Asset slice stored as a standalone cell:
 * native$0000 = Asset;
 * jetton$0001 workchain:int8 address:bits256 = Asset;
 *
 * Extra-currency assets are ignored — we only render TON and jetton pairs.
 *
 * @param  {String} boc
 * @return {{ type: 'native'|'jetton', address: String|null }|null}
 */
export const parseDedustAsset = function parseDedustAssetCell(boc) {
    if (typeof boc !== 'string' || boc.length === 0) {
        return null;
    }

    let bits;

    try {
        bits = readRootCellBits(boc);
    } catch (error) {
        console.error('Failed to read DeDust asset cell:', error);
        return null;
    }

    if (!bits || bits.length < 4) {
        return null;
    }

    const kind = parseInt(bits.slice(0, 4), 2);

    if (kind === 0) {
        return { type: 'native', address: null };
    }

    if (kind !== 1 || bits.length < 4 + 8 + 256) {
        return null;
    }

    const rawWorkchain = parseInt(bits.slice(4, 12), 2);
    const workchain = rawWorkchain > 127 ? rawWorkchain - 256 : rawWorkchain;

    let hash = '';
    for (let i = 0; i < 32; i += 1) {
        const offset = 12 + (i * 8);
        hash += parseInt(bits.slice(offset, offset + 8), 2).toString(16).padStart(2, '0');
    }

    try {
        return { type: 'jetton', address: canonizeAddress(`${workchain}:${hash}`) };
    } catch (error) {
        console.error('Failed to canonize DeDust jetton asset:', error);
        return null;
    }
};
